// Notification utility functions for Certs

import { API_BASE_URL, getAuthToken } from './api';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  actionRequired: boolean;
  actionData?: any;
  data?: any;
}

// Fetch notifications
export const fetchNotifications = async (params: {
  page?: number;
  limit?: number;
  type?: string;
  unread_only?: boolean;
} = {}): Promise<{ data: Notification[]; unreadCount: number }> => {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.type) queryParams.append('type', params.type);
  if (params.unread_only) queryParams.append('unread_only', 'true');

  const response = await fetch(`${API_BASE_URL}/notifications?${queryParams}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch notifications');
  }

  return response.json();
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to mark notification as read');
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to mark all notifications as read');
  }
};

// Handle notification action (Accept/Decline)
export const handleNotificationAction = async (
  notificationId: string, 
  action: 'accept' | 'decline'
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/action`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ action })
  });

  if (!response.ok) {
    throw new Error('Failed to handle notification action');
  }
};

// Create notification (for system use)
export const createNotification = async (
  userId: string,
  type: string,
  title: string,
  message: string,
  data: any = {},
  actionRequired: boolean = false,
  actionData: any = {}
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/notifications`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      userId,
      type,
      title,
      message,
      data,
      actionRequired,
      actionData
    })
  });

  if (!response.ok) {
    throw new Error('Failed to create notification');
  }
};

// Notification type helpers — keep in sync with backend
// src/modules/notifications/notifications.service.ts
export const NotificationTypes = {
  // Quotes
  QUOTE_SENT: 'quote_sent',
  QUOTE_ACCEPTED: 'quote_accepted',
  QUOTE_REJECTED: 'quote_rejected',
  QUOTE_CHANGES_REQUESTED: 'quote_changes_requested',

  // Invoices
  INVOICE_SENT: 'invoice_sent',
  INVOICE_PAID: 'invoice_paid',
  INVOICE_OVERDUE: 'invoice_overdue',
  INVOICE_VIEWED: 'invoice_viewed',

  // Jobs
  JOB_ASSIGNED: 'job_assigned',
  JOB_STARTED: 'job_started',
  JOB_COMPLETED: 'job_completed',
  JOB_CANCELLED: 'job_cancelled',

  // Certificates
  CERTIFICATE_ISSUED: 'certificate_issued',
  CERTIFICATE_EXPIRY: 'certificate_expiry',
  CERTIFICATE_SUBMITTED: 'certificate_submitted',

  // Team / portal
  TEAM_INVITE_ACCEPTED: 'team_invite_accepted',
  CLIENT_PORTAL_JOINED: 'client_portal_joined',

  // Generic / legacy
  SYSTEM_ALERT: 'system_alert',
  FOLLOW: 'follow',
  INVITE: 'invite',
  COMMENT: 'comment',
  SHARE: 'share',
} as const;

// Format time ago
export const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
  return `${Math.floor(diffInMinutes / 1440)} days ago`;
};

// Sample notifications for fallback/demo
export const getSampleNotifications = (): Notification[] => [
  {
    id: '1',
    type: NotificationTypes.FOLLOW,
    title: 'Jed followed you',
    message: '12 minutes ago',
    createdAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    read: false,
    actionRequired: false
  },
  {
    id: '2',
    type: NotificationTypes.INVITE,
    title: 'Ochieng invited you to 💎 Pebble UI',
    message: '1 hour ago',
    createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    read: false,
    actionRequired: true,
    actionData: { inviteId: 'inv-123', teamName: 'Pebble UI' }
  },
  {
    id: '3',
    type: NotificationTypes.COMMENT,
    title: 'Yolonda commented on your post',
    message: 'Wow, this looks absolutely stunning. Love the minimal approach you have, so clean. Great work, Ali ✨',
    createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    read: false,
    actionRequired: false
  },
  {
    id: '4',
    type: NotificationTypes.SHARE,
    title: 'Brion shared Variables file with you',
    message: 'variables.fig • 12.5 MB',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    read: true,
    actionRequired: false,
    data: { fileName: 'variables.fig', fileSize: '12.5 MB' }
  },
  {
    id: '5',
    type: NotificationTypes.JOB_ASSIGNED,
    title: 'New job assigned to you',
    message: 'Boiler service at 123 Oak Street - Emergency priority',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    read: false,
    actionRequired: false,
    data: { jobId: 'job-001', clientName: 'John Smith', address: '123 Oak Street', priority: 'emergency' }
  },
  {
    id: '6',
    type: NotificationTypes.CERTIFICATE_EXPIRY,
    title: 'Certificate expiring soon',
    message: 'Gas Safety Certificate for Wilson Estates expires in 7 days',
    createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    read: false,
    actionRequired: true,
    data: { certificateId: 'cert-456', clientName: 'Wilson Estates', expiryDate: '2024-11-14', daysLeft: 7 }
  }
];
